import React, { useState } from "react";
import { Box, IconButton, Typography, TextField, Table, TableHead, TableBody, TableRow, TableCell } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import axiosInstance from "utilities/axios";
import useDialog from "hooks/useDialog";
import FileUploadCGS from "components/CustomComponents/FileUploadCGS";
import FileDownloader from "components/CustomComponents/FileDownloadCGS";

interface AttachmentRow {
  file_name: string; // user-entered label, verbatim — same convention as DynamicFieldArray
  path: string | null;
  signed_url?: string;
}

interface AgencyDocumentsUploaderProps {
  applicationId: string;
  component: string;
  title: string;
  externalClasses?: any;
  initialFiles?: AttachmentRow[];
  // Uploaded files are visible to everyone; only users with this permission
  // can add/rename/remove — so the upload row, name input, and action icons
  // are hidden entirely for anyone else, leaving a plain read-only list.
  canEdit: boolean;
}

const AgencyDocumentsUploader: React.FC<AgencyDocumentsUploaderProps> = ({
  applicationId,
  component,
  title,
  externalClasses,
  initialFiles = [],
  canEdit,
}) => {
  const { showSnackbar, promptConfirm } = useDialog();
  const [rows, setRows] = useState<AttachmentRow[]>(initialFiles);

  const handleUploadSuccess = async (index: number, data: any) => {
    const file = data.additional_documents?.[0];
    if (!file) return;
    const fileName = rows[index].file_name.trim();

    try {
      const res = await axiosInstance.post(`/agency/submit_additional_documents/${component}/${applicationId}`, {
        attachments: [{ fileName, url: file.signed_url, s3Key: file.original_path }],
      });
      if (res.data.message) showSnackbar(res.data.message, "success");
      setRows((prev) => prev.map((row, i) => (i === index ? { file_name: fileName, path: file.original_path, signed_url: file.signed_url } : row)));
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || "დაფიქსირდა შეცდომა", "error");
    }
  };

  const handleUploadError = (error: any) => {
    showSnackbar(error?.response?.data?.message || "ფაილის ატვირთვა ვერ მოხერხდა", "error");
  };

  const handleRemove = async (index: number) => {
    const row = rows[index];

    if (!row.path) {
      setRows((prev) => prev.filter((_, i) => i !== index));
      return;
    }

    const confirmed = await promptConfirm({ title: "დადასტურება", message: "გსურთ ფაილის წაშლა?" });
    if (!confirmed) return;

    try {
      await axiosInstance.delete(`/agency/additional_documents/${applicationId}`, { data: { path: row.path } });
      setRows((prev) => prev.filter((_, i) => i !== index));
      showSnackbar("ფაილი წაიშალა", "success");
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || "დაფიქსირდა შეცდომა", "error");
    }
  };

  const handleAdd = () => setRows((prev) => [...prev, { file_name: "", path: null }]);

  const updateName = (index: number, file_name: string) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, file_name } : row)));
  };

  // Anyone can see already-uploaded files; only canEdit users see pending
  // (not-yet-uploaded) rows, since those are mid-edit and not real files yet.
  const visibleRows = canEdit ? rows : rows.filter((row) => row.path);

  return (
    <Box>
      <Typography className={externalClasses?.cardSummary}>{title}</Typography>

      {visibleRows.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell style={{ width: 32, fontSize: 11 }}>#</TableCell>
              <TableCell style={{ fontSize: 11 }}>დასახელება</TableCell>
              <TableCell style={{ width: 80, fontSize: 11 }} align="right">
                მოქმედება
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row, index) => (
              <TableRow key={index}>
                <TableCell style={{ fontSize: 12 }}>{index + 1}</TableCell>
                <TableCell style={{ fontSize: 12 }}>
                  {!row.path && canEdit ? (
                    <TextField
                      value={row.file_name}
                      onChange={(e) => updateName(rows.indexOf(row), e.target.value)}
                      required
                      error={!row.file_name.trim()}
                      size="small"
                      placeholder="დოკუმენტის დასახელება *"
                      InputProps={{ sx: { height: "36px" } }}
                      inputProps={{ style: { fontSize: 12 } }}
                    />
                  ) : (
                    row.file_name
                  )}
                </TableCell>
                <TableCell align="right">
                  <Box display="flex" justifyContent="flex-end" alignItems="center">
                    {row.signed_url && row.path ? (
                      <FileDownloader url={row.signed_url} />
                    ) : (
                      canEdit && (
                        <FileUploadCGS
                          uploadUrl={`/agency/upload_additional_documents/${component}`}
                          fieldName="additional_documents"
                          disabled={!row.file_name.trim()}
                          onUploadSuccess={(data) => handleUploadSuccess(rows.indexOf(row), data)}
                          onUploadError={handleUploadError}
                        />
                      )
                    )}
                    {canEdit && (
                      <IconButton size="small" onClick={() => handleRemove(rows.indexOf(row))}>
                        <DeleteIcon sx={{ fontSize: 20, color: "#1976d2" }} />
                      </IconButton>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {canEdit && (
        <Box display="flex" justifyContent="flex-end" sx={{ mt: 1 }}>
          <IconButton size="small" sx={{ width: "20px", height: "20px", p: 0 }} onClick={handleAdd}>
            <AddIcon sx={{ fontSize: 20, color: "#1976d2" }} />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

export default AgencyDocumentsUploader;
